import { inject, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  extractObject,
  hydratedGlobalParams,
  normalizeFilterParams,
  sanitizeActionParams,
} from '../../shared/workspace-element-context';

const INTERNAL_API_PREFIXES = [
  '/gtw/',
  '/gtw-efile-api/',
  '/batch/',
  '/admin/',
  '/custom/',
  '/pdf-engine/',
  '/bulk-pdf-engine/',
  '/scheduler/',
];

let redirecting = false;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const isInternal = isInternalRequest(req);
  const headers: Record<string, string> = {};

  if (isInternal) {
    const clientKey = sessionStorage.getItem('client_key') || '';
    const authorization = sessionStorage.getItem('Authorization') || '';

    if (authorization && !req.url.includes('gtw-websockets')) {
      headers['Authorization'] = authorization;
    }

    if (clientKey) {
      headers['client_key'] = clientKey;
    }

    headers['X-Requested-With'] = 'XMLHttpRequest';
    headers['ist-req-tkn'] = generateRandomString(30);

    if (req.method !== 'GET' && req.method !== 'HEAD') {
      const xsrfToken = getCookie('XSRF-TOKEN');
      if (xsrfToken) {
        headers['X-XSRF-TOKEN'] = xsrfToken;
      }
    }
  }

  const requestBody = isInternal ? enrichSaveJsonObjectBody(req) : req.body;

  const cloned = req.clone({
    body: requestBody,
    setHeaders: headers,
    withCredentials: isInternal,
  });

  return next(cloned).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.headers?.get('is_maintenance') === 'true') {
        window.location.href = 'app/templates/maintenance.html';
        return throwError(() => error);
      }

      if (isInternal) {
        switch (error.status) {
          case 401:
            handle401();
            break;
          case 403:
          case 509:
            navigateToAppRoute('accessDenied');
            break;
          case 0:
            console.error('Network error: server unreachable', req.url);
            break;
        }
      }

      return throwError(() => error);
    })
  );
};

function isInternalRequest(req: HttpRequest<unknown>): boolean {
  const url = req.url;
  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url);
      if (parsed.origin !== window.location.origin) {
        return false;
      }
      return INTERNAL_API_PREFIXES.some((prefix) => parsed.pathname.startsWith(prefix));
    } catch {
      return false;
    }
  }
  return INTERNAL_API_PREFIXES.some((prefix) => url.startsWith(prefix));
}

function navigateToAppRoute(path: string): void {
  const normalized = path.replace(/^\//, '');
  window.location.assign('/' + normalized);
}

function handle401(): void {
  if (redirecting) {
    return;
  }
  redirecting = true;

  if (isLocalHost()) {
    redirecting = false;
    return;
  }

  sessionStorage.removeItem('Authorization');

  const envGateway = (window as any).__env?.gateWay;

  if (envGateway) {
    window.location.href = envGateway + '/gtw/oauth2/authorization/gtw-services';
  } else {
    navigateToAppRoute('loggedOut');
    redirecting = false;
  }
}

function isLocalHost(): boolean {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

function generateRandomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars[array[i] % chars.length];
  }
  return result;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(^|;\\s*)' + name + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[2]) : null;
}

function enrichSaveJsonObjectBody(req: HttpRequest<unknown>): unknown {
  if (!isSaveJsonObjectRequest(req)) {
    return req.body;
  }

  const injector = inject(Injector);
  const gtwWorkspace = injector.get(WorkspaceService);
  const gtwGlobal = injector.get(GlobalService);
  const gtwApp = injector.get(AppService);

  const activeScreen = gtwWorkspace.workspaceFactory?.activeScreen || {};
  const globalService = hydratedGlobalParams(gtwGlobal, gtwWorkspace);
  const filterParams = normalizeFilterParams(activeScreen);

  const missingParams: Record<string, unknown> = sanitizeActionParams({
    ...extractObject(globalService),
    ...extractObject(filterParams),
  });

  if (!missingParams.sso_id) {
    const ssoId = gtwApp.USER_SETTINGS?.user?.sso_id;
    if (ssoId) {
      missingParams.sso_id = ssoId;
    }
  }

  if (!Object.keys(missingParams).length) {
    return req.body;
  }

  const body = req.body;

  if (body === null || body === undefined) {
    return missingParams;
  }

  if (typeof body === 'string') {
    return mergeIntoEncodedString(body, missingParams);
  }

  if (body instanceof HttpParams) {
    return mergeIntoHttpParams(body, missingParams);
  }

  if (body instanceof URLSearchParams) {
    return mergeIntoUrlSearchParams(body, missingParams);
  }

  if (body instanceof FormData) {
    return mergeIntoFormData(body, missingParams);
  }

  if (typeof body === 'object') {
    return mergeIntoObject(body as Record<string, unknown>, missingParams);
  }

  return body;
}

function isSaveJsonObjectRequest(req: HttpRequest<unknown>): boolean {
  return req.url.includes('saveJsonObject') && req.method !== 'GET' && req.method !== 'HEAD';
}

function mergeIntoObject(
  body: Record<string, unknown>,
  params: Record<string, unknown>
): Record<string, unknown> {
  const merged = { ...body };
  Object.keys(params).forEach((key) => {
    if (!hasOwnBodyValue(merged[key])) {
      merged[key] = params[key];
    }
  });
  return merged;
}

function mergeIntoEncodedString(body: string, params: Record<string, unknown>): string {
  const encoded = new URLSearchParams(body);
  Object.keys(params).forEach((key) => {
    const currentValue = encoded.get(key);
    if (!hasOwnBodyValue(currentValue)) {
      encoded.set(key, serializeParamValue(params[key]));
    }
  });
  return encoded.toString();
}

function mergeIntoHttpParams(body: HttpParams, params: Record<string, unknown>): HttpParams {
  let merged = body;
  Object.keys(params).forEach((key) => {
    const currentValue = merged.get(key);
    if (!hasOwnBodyValue(currentValue)) {
      merged = merged.set(key, serializeParamValue(params[key]));
    }
  });
  return merged;
}

function mergeIntoUrlSearchParams(
  body: URLSearchParams,
  params: Record<string, unknown>
): URLSearchParams {
  const merged = new URLSearchParams(body.toString());
  Object.keys(params).forEach((key) => {
    const currentValue = merged.get(key);
    if (!hasOwnBodyValue(currentValue)) {
      merged.set(key, serializeParamValue(params[key]));
    }
  });
  return merged;
}

function mergeIntoFormData(body: FormData, params: Record<string, unknown>): FormData {
  const merged = new FormData();
  body.forEach((value, key) => {
    merged.append(key, value);
  });

  Object.keys(params).forEach((key) => {
    if (!hasOwnBodyValue(merged.get(key))) {
      merged.append(key, serializeParamValue(params[key]));
    }
  });

  return merged;
}

function hasOwnBodyValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

function serializeParamValue(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value === null || value === undefined) {
    return '';
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}
